import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from "react-oidc-context";
import Navigation from './components/Navigation';
import DataUpload from './pages/DataUpload';
import Dashboard from './pages/Dashboard';
import Predictions from './pages/Predictions';
import LivePreds from './pages/LivePreds';
import Data from './pages/Data';
import BaseModel from './pages/BaseModel';
import DailyAnalysis from './pages/DailyAnalysis';
import Diagnosis from './pages/Diagnosis';
import CombinedModels from './pages/CombinedModels';
import CombinedStepModels from './pages/CombinedStepModels';
import ProbabilisticModel from './pages/ProbabilisticModel';
import ProbStepModel from './pages/ProbStepModel';
import IDA1Model from './pages/IDA1-Model';
import BisonModel from './pages/BisonModel';
import GooseModel from './pages/GooseModel';
import GooseIDA1Model from './pages/IDA1-Goose-Model';
import OrderBuilder from './pages/OrderBuilder';
import ProbabilisticOrderBuilder from './pages/ProbabilisticOrderBuilder';
import BisonOrderBuilder from './pages/BisonOrderBuilder';
import GooseOrderBuilder from './pages/GooseOrderBuilder';
import Schedule from './pages/Schedule';
import BisonSchedule from './pages/BisonSchedule';
import MarketInfluence from './pages/MarketInfluence';
import Results from './pages/Results';
import Fix2V5Sim from './pages/Fix2V5Sim';
import Prices from './pages/Prices';
import EuropeMap from './pages/EuropeMap';
import Logs from './pages/Logs';
// User-specific pages
import UserPredictions from './pages/user/Predictions';
import UserOrderBuilder from './pages/user/OrderBuilder';

const GLOBAL_DARK_MODE_COOKIE = 'site_dark_mode';

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
};

const setCookieValue = (name, value, maxAgeSeconds = 60 * 60 * 24 * 365) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

const App = () => {
  const auth = useAuth();
  const [darkMode, setDarkMode] = useState(() => getCookieValue(GLOBAL_DARK_MODE_COOKIE) === '1');

  useEffect(() => {
    setCookieValue(GLOBAL_DARK_MODE_COOKIE, darkMode ? '1' : '0');
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('global-dark-mode', darkMode);
    return () => {
      document.body.classList.remove('global-dark-mode');
    };
  }, [darkMode]);

  const signOutRedirect = () => {
    const clientId = "42m0lftgoms9389ju2q04hmboi";
    const logoutUri = "https://algoenergies.com/";
    const cognitoDomain = "https://eu-west-1tuyaq7gb3.auth.eu-west-1.amazoncognito.com";
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  const handleLogout = () => {
    auth.removeUser();
    signOutRedirect();
  };

  if (auth.isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-slate-950 text-slate-200' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
        <div className={darkMode ? 'text-slate-300' : 'text-gray-600'}>Loading...</div>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
        <div className="text-center">
          <div className="text-red-600 mb-4">Authentication error: {auth.error.message}</div>
          <button
            onClick={() => auth.signinRedirect()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (auth.isAuthenticated) {
    const username = auth.user?.profile?.email || 'User';
    // Get user groups from Cognito claims
    const userGroups = auth.user?.profile?.['cognito:groups'] || [];
    const isAdmin = userGroups.includes('admin');

    // Define which routes are available for regular users
    const defaultPath = isAdmin ? '/' : '/predictions';

    return (
      <Router>
        <div className={`min-h-screen ${darkMode ? 'bg-slate-950 text-slate-100' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
          <Navigation
            username={username}
            onLogout={handleLogout}
            isAdmin={isAdmin}
            darkMode={darkMode}
            onToggleDarkMode={() => setDarkMode((prev) => !prev)}
          />

          <div className="max-w-[1600px] mx-auto p-6">
            <div className={`${darkMode ? 'bg-slate-900 text-slate-100 border border-slate-800' : 'bg-white'} rounded-2xl shadow-xl p-8`}>
              <Routes>
                {/* Routes for regular users */}
                {!isAdmin && (
                  <>
                    <Route path="/predictions" element={<UserPredictions />} />
                    <Route path="/order-builder" element={<UserOrderBuilder />} />
                  </>
                )}

                {/* Admin routes - full access */}
                {isAdmin && (
                  <>
                    <Route path="/" element={<Dashboard darkMode={darkMode} />} />
                    <Route path="/upload" element={<DataUpload />} />
                    <Route path="/predictions" element={<Predictions />} />
                    <Route path="/live-preds" element={<LivePreds />} />
                    <Route path="/order-builder" element={<OrderBuilder />} />
                    <Route path="/data" element={<Data />} />
                    <Route path="/base-model" element={<BaseModel />} />
                    <Route path="/diagnosis" element={<Diagnosis />} />
                    <Route path="/daily-analysis" element={<DailyAnalysis />} />
                    <Route path="/prices" element={<Prices />} />
                    <Route path="/europe-map" element={<EuropeMap darkMode={darkMode} />} />
                    <Route path="/logs" element={<Logs />} />
                    <Route path="/combined-models" element={<CombinedModels />} />
                    <Route path="/combined-step-models" element={<CombinedStepModels />} />
                    <Route path="/probabilistic-model" element={<ProbabilisticModel />} />
                    <Route path="/prob-step-model" element={<ProbStepModel />} />
                    <Route path="/bison-model" element={<BisonModel />} />
                    <Route path="/ida1-model" element={<IDA1Model />} />
                    <Route path="/goose-model" element={<GooseModel />} />
                    <Route path="/goose-ida1" element={<GooseIDA1Model />} />
                    <Route path="/probabilistic-order-builder" element={<ProbabilisticOrderBuilder />} />
                    <Route path="/bison-order-builder" element={<BisonOrderBuilder />} />
                    <Route path="/goose-order-builder" element={<GooseOrderBuilder />} />
                    <Route path="/schedule" element={<Schedule />} />
                    <Route path="/bison-schedule" element={<BisonSchedule />} />
                    <Route path="/goose-schedule" element={<BisonSchedule />} />
                    <Route path="/market-influence" element={<MarketInfluence />} />
                    <Route path="/results" element={<Results darkMode={darkMode} />} />
                    <Route path="/fix2-v5-sim/*" element={<Fix2V5Sim darkMode={darkMode} userEmail={username} />} />
                  </>
                )}

                {/* Default redirect based on role */}
                <Route path="*" element={<Navigate to={defaultPath} replace />} />
              </Routes>
            </div>
          </div>
        </div>
      </Router>
    );
  }

  // Not authenticated - show login page
  return (
    <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className={`${darkMode ? 'bg-slate-900 text-slate-100 border border-slate-800' : 'bg-white'} rounded-2xl shadow-xl p-8 max-w-md w-full mx-4`}>
        <div className="text-center mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>Puzzle Trading</h1>
        </div>
        <button
          onClick={() => auth.signinRedirect()}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
        >
          Sign in
        </button>
      </div>
    </div>
  );
};

export default App;
